/**
 * One honest description of the trial offer, built from what the API actually returns.
 *
 * The free trial takes no card (Oct 2026) and opens everything a plan does. When it ends, access
 * pauses until a plan is chosen; nothing starts or is charged by itself without a card.
 * Anything shown to a buyer about the trial must come from here.
 *
 * `trial_terms` is served by GET /mobile/plans and GET student/subscription/plans
 * (MobileTrialLifecycleService::trialTermsPayload).
 */

/** Used only if the API has not answered yet — deliberately vaguer, never a stronger claim. */
const FALLBACK = {
  trial_days: 7,
  card_required: false,
  grace_days: 1,
  grace_hours: 24,
  access_ends_with_trial: true,
  auto_renew_plan: null,
};

const dayWord = (n) => (Number(n) === 1 ? "1 day" : `${n} days`);

/** The window between the trial ending and the plan starting: days from the API, else hours. */
const graceText = (t) =>
  t.grace_days ? dayWord(t.grace_days) : `${t.grace_hours || 24} hours`;

/** "£1.00 a week" from the plan's price and interval. */
export function describePlanPrice(plan) {
  if (!plan) return null;
  const price = plan.price_display || "";
  const interval = String(plan.billing_interval || "").toLowerCase();
  if (!price) return null;
  const per = { week: "a week", month: "a month", year: "a year" }[interval];
  return per ? `${price} ${per}` : `${price} per ${interval || "period"}`;
}

/**
 * The full terms sentence for a page that sells the trial.
 *
 * @param {object|null} terms `trial_terms` from the API
 * @returns {string}
 */
export function trialTermsSentence(terms) {
  const t = terms || FALLBACK;
  const card = t.card_required
    ? "A payment card is required to start it, and you are not charged during the trial."
    : "No payment card is needed to start it.";

  const plan = describePlanPrice(t.auto_renew_plan);
  const graceDays = Number(t.grace_days || 0);
  const graceHours = Number(t.grace_hours || 0);
  // Current offer: access stops the moment the trial ends. The window after it only delays
  // starting a plan on a saved card, so it is not described as extra time to use the app.
  const pause = "Everything is open during the trial. When it ends, access pauses until you choose a plan.";
  const next = t.access_ends_with_trial
    ? plan && t.card_required
      ? `${pause} If you do not choose one, the ${t.auto_renew_plan.name} plan (${plan}) starts on your saved card after ${graceText(t)}.`
      : `${pause} Nothing is charged unless you choose a plan.`
    : !plan
    ? graceDays > 0 || graceHours > 0
      ? `When the trial ends you have ${graceText(t)} to choose a plan and add your card, and we email you a reminder. Nothing is charged unless you choose a plan.`
      : "When the trial ends you choose a plan before any payment is taken."
    : graceDays > 0 || graceHours > 0
      ? `When the trial ends you have ${graceText(t)} to choose a plan, and we email you a reminder. After that, the plan you chose starts on your saved card, or the ${t.auto_renew_plan.name} plan (${plan}) if you did not choose one.`
      : `We email you a reminder the day before it ends. When the trial ends, the plan you chose starts on your saved card, or the ${t.auto_renew_plan.name} plan (${plan}) if you did not choose one.`;

  return `Start with ${dayWord(t.trial_days)} free. ${card} ${next} Cancel any time from your account.`;
}

/** Short version for a card or a button caption. */
export function trialTermsShort(terms) {
  const t = terms || FALLBACK;
  const plan = describePlanPrice(t.auto_renew_plan);
  const card = t.card_required ? "card required" : "no card needed";
  return plan
    ? `${dayWord(t.trial_days)} free, ${card}. Then your chosen plan (${plan} if none) unless you cancel.`
    : `${dayWord(t.trial_days)} free, ${card}. Cancel any time.`;
}

/** Renewal terms for a paid plan card, e.g. "Renews at £29.99 a month until you cancel." */
export function renewalSentence(plan) {
  const price = describePlanPrice(plan);
  return price ? `Renews at ${price} until you cancel.` : "Renews automatically until you cancel.";
}

export { FALLBACK as FALLBACK_TRIAL_TERMS };
